import { useState } from 'react'
import { Checkbox, Radio, RadioChangeEvent, Spin } from 'antd'
import CustomModal from 'components/global/CustomModal'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'
import { convertToLocalRegistrationTime } from 'helper/utility'
import { LeagueAttendanceExportResp } from 'types/league/leagueAttendance'

interface Props {
  open: boolean
  onClose: () => void
  data: LeagueAttendanceExportResp[]
  loading: boolean
}

const ALL_COLUMNS = [
  'Profile Photo',
  'Name',
  'Age',
  'Date of Birth',
  'Type',
  'Gender',
  'Registration Date',
  'Registration Time',
  'Organization',
  'Team',
  'Division',
  'Days',
  'Allergies',
  'Guardians',
  'Adults',
  'Host Org',
  'Player Org',
  'League Name',
  'Sport',
  'Year',
  'Season',
]

const headers = [
  { label: 'Profile Photo',     key: 'profilePhoto' },
  { label: 'Name',              key: 'name' },
  { label: 'Age',               key: 'age' },
  { label: 'Date of Birth',     key: 'dob' },
  { label: 'Type',              key: 'type' },
  { label: 'Gender',            key: 'gender' },
  { label: 'Registration Date', key: 'registrationDate' },
  { label: 'Registration Time', key: 'registrationTime' },
  { label: 'Organization',      key: 'organization' },
  { label: 'Team',              key: 'team' },
  { label: 'Division',          key: 'division' },
  { label: 'Days',              key: 'days' },
  { label: 'Allergies',         key: 'allergies' },
  { label: 'Guardians',         key: 'guardians' },
  { label: 'Adults',            key: 'adults' },
  { label: 'Host Org',          key: 'hostOrg' },
  { label: 'Player Org',        key: 'playerOrg' },
  { label: 'League Name',       key: 'leagueName' },
  { label: 'Sport',             key: 'sport' },
  { label: 'Year',              key: 'year' },
  { label: 'Season',            key: 'season' },
]

const ExportLeagueAttendeesModal: React.FC<Props> = ({ open, onClose, data, loading }) => {
  const [fileFormat, setFileFormat] = useState('csv')
  const [selectedColumns, setSelectedColumns] = useState<string[]>(ALL_COLUMNS)

  const buildRows = () => {
    const filteredHeaders = headers.filter(h => selectedColumns.includes(h.label))
    return data.map(item => {
      const { registrationDate, registrationTime } = convertToLocalRegistrationTime(item?.registrationTime)
      const full: Record<string, any> = {
        profilePhoto:     item?.profilePhoto ?? '',
        name:             item?.name ?? '',
        age:              item?.age ?? '',
        dob:              item?.dob ? moment(item.dob).format('MMM/DD/YYYY') : '',
        type:             item?.type ?? '',
        gender:           item?.gender ?? '',
        registrationDate,
        registrationTime,
        organization:     item?.organization ?? '',
        team:             item?.team ?? '',
        division:         item?.division ?? '',
        days:             item?.days ?? '',
        allergies:        item?.allergies ?? '',
        guardians:        item?.guardians ?? '',
        adults:           item?.adults ?? '',
        hostOrg:          item?.hostOrg ?? '',
        playerOrg:        item?.playerOrg ?? '',
        leagueName:       item?.leagueName ?? '',
        sport:            item?.sport ?? '',
        year:             item?.year ?? '',
        season:           item?.season ?? '',
      }
      const out: Record<string, any> = {}
      filteredHeaders.forEach(h => { out[h.key] = full[h.key] })
      return out
    })
  }

  const handleFormatChange = (e: RadioChangeEvent) => setFileFormat(e.target.value)

  const handleColumnChange = (checked: boolean, label: string) => {
    setSelectedColumns(prev =>
      checked ? [...prev, label] : prev.filter(c => c !== label),
    )
  }

  const handleExport = () => {
    const filteredHeaders = headers.filter(h => selectedColumns.includes(h.label))
    const rows = buildRows()

    if (fileFormat === 'csv') {
      const escape = (v: any) => {
        const s = String(v ?? '')
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s
      }
      const csv = [
        filteredHeaders.map(h => escape(h.label)).join(','),
        ...rows.map(row => filteredHeaders.map(h => escape(row[h.key])).join(',')),
      ].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'League_Attendees.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } else if (fileFormat === 'excel') {
      // Use array-of-arrays so display labels become column headers
      const aoa: any[][] = [
        filteredHeaders.map(h => h.label),
        ...rows.map(row => filteredHeaders.map(h => {
          if (h.key === 'profilePhoto' && row[h.key]) {
            return { f: `HYPERLINK("${row[h.key]}", "Profile Photo")` }
          }
          return row[h.key]
        })),
      ]
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, 'League_Attendees.xlsx')
    } else if (fileFormat === 'pdf') {
      const doc = new jsPDF()
      const cols = filteredHeaders.map(h => h.label)
      const pdfRows = rows.map(row => filteredHeaders.map(h => String(row[h.key] ?? '')))
      ;(doc as any).autoTable({ head: [cols], body: pdfRows })
      doc.save('League_Attendees.pdf')
    }

    onClose()
  }

  return (
    <CustomModal
      openModal={open}
      closeModal={onClose}
      width={600}
      title="Export Attendees"
      footer={false}
    >
      {loading ? (
        <div className="flex justify-center py-40px">
          <Spin />
        </div>
      ) : (
        <>
          <div className="formatText">Format</div>
          <Radio.Group onChange={handleFormatChange} value={fileFormat}>
            <Radio value="csv">CSV</Radio>
            <Radio value="excel">Excel</Radio>
          </Radio.Group>

          <div className="radioText mt-15px">Please select columns to export</div>
          <div className="checkbox-group">
            {ALL_COLUMNS.map(label => (
              <div key={label}>
                <Checkbox
                  checked={selectedColumns.includes(label)}
                  onChange={e => handleColumnChange(e.target.checked, label)}
                >
                  {label}
                </Checkbox>
              </div>
            ))}
          </div>

          <div className="flex justify-end w-full mt-10px">
            <div className="bg-grey radius-8 pointer mr-30px">
              <button
                className="prim-sm-btn"
                style={{ background: '#EBEDF1', color: 'black' }}
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
            <button className="prim-sm-btn" onClick={handleExport}>
              Export
            </button>
          </div>
        </>
      )}
    </CustomModal>
  )
}

export default ExportLeagueAttendeesModal
